import React from 'react';
import { 
  Instagram, Facebook, Linkedin, Twitter, 
  MapPin, Phone, Mail, ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  // Social Media Links with Verified URLs
  const socialLinks = [
    { 
      icon: Instagram, 
      name: 'Instagram', 
      url: 'https://www.instagram.com/luxuriyahomes/' 
    },
    { 
      icon: Facebook, 
      name: 'Facebook', 
      url: 'https://www.facebook.com/luxuriyahomes' 
    },
    { 
      icon: Linkedin, 
      name: 'LinkedIn', 
      url: 'https://www.linkedin.com/company/luxuriyahomes' 
    }
  ];

  return (
    <footer className="bg-neutral-950 text-gray-300 pt-20 pb-10 border-t border-amber-900/30 font-sans relative overflow-hidden">
      
      {/* Decorative Gold Glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-amber-900/10 blur-[100px] pointer-events-none rounded-full"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* TOP SECTION: Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          
          {/* Column 1: Brand Info */}
          <div className="space-y-6">
            <div className="flex flex-col items-start">
               <span className="text-3xl text-amber-600 mb-1">♛</span>
               <h2 className="text-2xl font-serif font-bold text-white tracking-[0.2em] uppercase">
                 Luxuriya
               </h2>
               <span className="text-[0.6rem] tracking-[0.4em] text-gray-500 uppercase">
                 Homes
               </span>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed pr-4">
              Premium Interior Design & Architecture Firm serving Delhi NCR, Lucknow, and Varanasi since 2010.
            </p>
            
            <div className="flex space-x-4">
              {socialLinks.map((item, idx) => (
                <a 
                  key={idx} 
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Follow Luxuriya Homes on ${item.name}`}
                  className="w-10 h-10 rounded-full border border-gray-800 flex items-center justify-center hover:bg-amber-600 hover:border-amber-600 hover:text-white transition-all duration-300"
                >
                  <item.icon size={16} /> {/* Fixed icon rendering */}
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-white font-serif text-lg mb-6 tracking-wide">
              Quick Links
            </h3>
            <ul className="space-y-4 text-sm">
              {[
                { name: 'About Company', path: '/about' },
                { name: 'Our Projects', path: '/projects' },
                { name: 'Services', path: '/services' },
                { name: 'Contact Us', path: '/contact' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="hover:text-amber-500 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-[1px] bg-amber-500 transition-all duration-300"></span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <h3 className="text-white font-serif text-lg mb-6 tracking-wide">
              Our Services
            </h3>
            <ul className="space-y-4 text-sm">
              {[
                { name: 'Luxury Interiors', path: '/services/interior-design' },
                { name: 'Home Construction', path: '/services/builders' },
                { name: 'Modular Kitchens', path: '/gallery/kitchen' },
                { name: 'Office Design', path: '/gallery/luxury-office' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="hover:text-amber-500 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-[1px] bg-amber-500 transition-all duration-300"></span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact Info - SEO RICH */}
          <div>
            <h3 className="text-white font-serif text-lg mb-6 tracking-wide">Get in Touch</h3>
            <address className="space-y-4 text-sm mb-8 not-italic">
              {/* Noida Office */}
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-amber-600 mt-1 shrink-0" />
                <p>
                  <span className="block text-white font-bold">Head Office:</span>
                  Ithum Tower, Sector 62,<br />
                  Noida, Uttar Pradesh - 201309
                </p>
              </div>
              
              {/* Phone */}
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-amber-600 shrink-0" />
                <a href="tel:+917291832000" className="hover:text-amber-500 transition-colors font-bold text-white">
                  +91 7291 832 000
                </a>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-amber-600 shrink-0" />
                <a href="mailto:hello@luxuriyahomes.com" className="hover:text-amber-500 transition-colors">
                  luxuriyahomes@gmail.com
                </a>
              </div>
            </address>

            {/* Newsletter Input */}
            <form className="relative" onSubmit={(e) => e.preventDefault()}>
              <label htmlFor="newsletter-email" className="sr-only">Your Email Address</label>
              <input 
                id="newsletter-email"
                type="email" 
                placeholder="Enter Email for Design Tips" 
                className="w-full bg-neutral-900 border-b border-gray-700 py-3 pl-2 pr-10 text-sm focus:outline-none focus:border-amber-600 transition-colors text-white placeholder-gray-600"
              />
              <button 
                type="submit"
                aria-label="Subscribe"
                className="absolute right-0 top-1/2 -translate-y-1/2 text-amber-600 hover:text-white transition-colors"
              >
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>

        {/* BOTTOM BAR */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs tracking-wider text-gray-500">
          <p>© 2026 Luxuriya Homes. Serving Delhi, Lucknow & Varanasi.</p>
          <div className="flex space-x-8">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-condition" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;